import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import usePrevious from "hooks/usePrevious";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";
import { shouldContinuePolling } from "utils/run";
import Run from "components/Run";

import { ModuleContext } from "../Context";
import { GET_RUN } from "./gql";

// MODULE PRs RUN
const ModuleRun = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();

  const { reportSuccess } = useTypedContext(FlashContext);
  const { module, moduleUrl } = useTypedContext(ModuleContext);

  const { data, error, loading } = usePolledQuery<{ module: Module }>(GET_RUN, {
    pollingGroup: PollingIntervalGroups.SingleEntities,
    shouldPoll: (data) => {
      if (!data?.module?.run) return false;
      return shouldContinuePolling(data.module.run);
    },
    fetchPolicy: "network-only",
    variables: { moduleId: module.id, runId },
    // APOLLO CLIENT UPDATE
  });

  // fixes browser back case
  const previousRunID = usePrevious(runId);
  const previousNextValue = usePrevious<boolean | "loading">(
    data?.module?.run && runId === previousRunID ? !!data?.module?.run?.next?.id : "loading",
    runId !== previousRunID
  );

  const handleRedirect = (runUrl: string) => {
    navigate(runUrl);
  };

  useEffect(() => {
    if (previousNextValue !== "loading") {
      if (!previousNextValue && !!data?.module?.run?.next?.id) {
        const redirectUrl = `${moduleUrl}/run/${data?.module?.run?.next?.id}`;
        reportSuccess({
          message: "A newer run is now available",
          actionTitle: "Go to run",
          actionCallback: () => handleRedirect(redirectUrl),
          timeout: 30000,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousNextValue, data?.module?.run?.next?.id]);

  useBreadcrumbs(
    [
      {
        title: "Modules",
        link: "/modules",
      },
      {
        title: module.id,
        link: `/module/${module.id}`,
      },
      {
        title: data?.module?.run?.title || "",
      },
    ],
    [data?.module?.run?.title]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module) {
    return <PageLoading />;
  }

  if (!data?.module?.run) {
    return <NotFoundPage />;
  }

  return <Run run={data?.module?.run} stack={module} />;
};

export default ModuleRun;
